import { useEffect, useMemo, useRef, useState } from "react";
import { alfredService, KycFallback } from "src/api";
import { getKycFieldMeta } from "src/components/UI/alfred-kyc-fallback/kyc-field-meta";

interface UseAlfredKycFallbackParams {
  fallback: KycFallback;
}

const POLL_INTERVAL_MS = 5000;

/**
 * Drives the KYC update-required correction form: holds the text/date/
 * boolean corrections + per-document File handles + touched state, builds
 * the multipart submit, and — once submitted — polls eligibility every
 * ~5s until the gate moves off KYC_UPDATE_REQUIRED (verified or a
 * different blocking state). Mirrors useAlfredSupportedOptions' lifecycle.
 */
export const useAlfredKycFallback = ({ fallback }: UseAlfredKycFallbackParams) => {
  // Seed text/date/boolean corrections from each field's prefill. File
  // uploads never carry a prefill — they live in `documents`.
  const initialCorrections = useMemo(() => {
    const seed: Record<string, string> = {};
    fallback.fields.forEach((field) => {
      if (field.type === "FILE_UPLOAD") return;
      if (field.prefill === undefined) return;
      // Sanitize the prefill the same way typed input is masked, so a stored
      // value with dashes/spaces (e.g. "123.456.789-09") lands clean.
      const meta = getKycFieldMeta(field.fieldName, fallback.country);
      seed[field.fieldName] = meta.sanitize ? meta.sanitize(field.prefill) : field.prefill;
    });
    return seed;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [corrections, setCorrections] = useState<Record<string, string>>(initialCorrections);
  const [documents, setDocuments] = useState<Record<string, File>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const setCorrection = (fieldName: string, value: string) =>
    setCorrections((prev) => ({ ...prev, [fieldName]: value }));
  const setDocument = (fieldName: string, file: File | null) =>
    setDocuments((prev) => {
      const next = { ...prev };
      if (file) next[fieldName] = file;
      else delete next[fieldName];
      return next;
    });
  const markTouched = (fieldName: string) =>
    setTouched((prev) => ({ ...prev, [fieldName]: true }));

  // Required text/date fields must be non-empty; required documents must
  // have a file. Booleans are always satisfiable.
  const fieldErrors = useMemo<Record<string, string>>(() => {
    const errors: Record<string, string> = {};
    fallback.fields.forEach((field) => {
      if (field.type === "FILE_UPLOAD") return; // validated via documents
      if (field.type === "BOOLEAN") return; // always satisfiable
      const value = (corrections[field.fieldName] ?? "").trim();
      if (!value) {
        if (field.required) errors[field.fieldName] = "This field is required";
        return;
      }
      // Per-field / per-country format check (lenient — only flags clearly-wrong input).
      const formatError = getKycFieldMeta(field.fieldName, fallback.country).validate?.(value);
      if (formatError) errors[field.fieldName] = formatError;
    });
    fallback.documents.forEach((doc) => {
      if (!documents[doc.fieldName]) errors[doc.fieldName] = "A document is required";
    });
    return errors;
  }, [fallback, corrections, documents]);

  const canSubmit = Object.keys(fieldErrors).length === 0 && !submitting && !submitted;

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  useEffect(() => () => stopPolling(), []);

  const startPolling = () => {
    stopPolling();
    pollRef.current = setInterval(async () => {
      try {
        const eligibility = await alfredService.getEligibility();
        const stillFallback =
          !eligibility.eligible && eligibility.code.startsWith("KYC_UPDATE_REQUIRED");
        if (!stillFallback) stopPolling();
      } catch (err) {
        // Transient poll failure — keep looping; the next tick retries.
        console.warn("KYC fallback eligibility poll failed:", err);
      }
    }, POLL_INTERVAL_MS);
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      // Reveal every error by marking all collectable fields touched.
      const next: Record<string, boolean> = {};
      fallback.fields.forEach((f) => (next[f.fieldName] = true));
      fallback.documents.forEach((d) => (next[d.fieldName] = true));
      setTouched(next);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const response = await alfredService.submitKycFallback({ corrections, documents });
      if (response.status === "submitted") {
        setSubmitted(true);
        startPolling();
      } else {
        setError(response.message ?? "Submission failed. Please try again.");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return {
    corrections,
    documents,
    touched,
    fieldErrors,
    submitting,
    submitted,
    error,
    canSubmit,
    setCorrection,
    setDocument,
    markTouched,
    handleSubmit,
  };
};
